import { useLayoutEffect, useRef } from "react";
import { Button, Icons, PocketPanel, PocketSection } from "@/components/ui";
import type { Task } from "@/api/tasks";
import { formatDate, useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { ListViewRow } from "./list-view-row";
import { localDateKey } from "./task-dates";

export function CalendarDayPocket({
  date,
  tasks,
  onSelectTask,
  onClose,
}: {
  date: Date;
  tasks: Task[];
  onSelectTask: (taskId: string) => void;
  onClose: () => void;
}) {
  const t = useT(framesTaskboardMessages);
  const root = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = root.current;
    const previous = document.activeElement;
    element?.querySelector<HTMLButtonElement>("button")?.focus();
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Element;
      if (
        root.current?.contains(target) ||
        target.closest("[data-task-pocket-owned]")
      )
        return;
      onClose();
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      if (
        element?.contains(document.activeElement) &&
        previous instanceof HTMLElement
      )
        previous.focus();
    };
  }, [onClose]);
  return (
    <div
      ref={root}
      className="absolute right-0 top-0 z-50 max-w-full"
      data-task-pocket-owned=""
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <PocketPanel
        nativeSurface={false}
        nativeGroup={`calendar-day-${localDateKey(date)}`}
        testId="calendar-day-pocket"
        data-denker-glass-force="css"
        className="w-80 max-w-full max-h-80 overflow-auto"
      >
        <PocketSection
          title={formatDate(date, { month: "short", day: "numeric" })}
          action={
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              aria-label={t("closeDay")}
              data-testid="calendar-day-close"
              onClick={onClose}
            >
              <Icons.X className="h-3.5 w-3.5" />
            </Button>
          }
        >
          {tasks.map((task) => (
            <ListViewRow key={task.id} task={task} onSelect={onSelectTask} />
          ))}
        </PocketSection>
      </PocketPanel>
    </div>
  );
}
